import { useState, useRef, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Camera, Plus } from 'lucide-react';
import { apiUrl } from '../../utils/api';

interface CommitmentSummary {
  pending: number;
  verified: number;
  delivered: number;
}

interface PhotoFile {
  file: File;
  preview: string;
}

interface Props {
  isOpen: boolean;
  campaignId: string;
  campaignTitle: string;
  commitments?: { status: string }[];
  onClose: () => void;
  onSuccess: () => void;
}

async function compressImage(file: File, maxSizeKB: number): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      const maxWidth = 1200;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      let quality = 0.8;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (blob && (blob.size <= maxSizeKB * 1024 || quality <= 0.3)) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              quality -= 0.1;
              tryCompress();
            }
          },
          'image/jpeg',
          quality
        );
      };
      tryCompress();
    };
    img.src = URL.createObjectURL(file);
  });
}

export function CompleteCampaignModal({ isOpen, campaignId, campaignTitle, commitments: propCommitments = [], onClose, onSuccess }: Props) {
  const [completionMessage, setCompletionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [noDeliveries, setNoDeliveries] = useState(false);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [existingEvidenceCount, setExistingEvidenceCount] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const [fetchedCommitments, setFetchedCommitments] = useState<{ status: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use prop commitments if available, otherwise use fetched ones
  const commitments = propCommitments.length > 0 ? propCommitments : fetchedCommitments;

  // Calculate commitment summary
  const commitmentSummary: CommitmentSummary = {
    pending: commitments.filter(c => c.status === 'pending').length,
    verified: commitments.filter(c => c.status === 'verified').length,
    delivered: commitments.filter(c => c.status === 'delivered').length,
  };

  const hasUnresolvedCommitments = commitmentSummary.pending > 0 || commitmentSummary.verified > 0;
  const hasDeliveries = commitmentSummary.delivered > 0;
  const photosRequired = hasDeliveries && !noDeliveries;
  const minPhotos = 3;
  const maxPhotos = 5 - existingEvidenceCount;
  const totalPhotos = photos.length + existingEvidenceCount;

  // Load existing evidence count and commitments on open
  useEffect(() => {
    if (!isOpen) return;
    setLoadingData(true);

    const loadData = async () => {
      try {
        // Load commitments if not provided via props
        if (propCommitments.length === 0) {
          const token = localStorage.getItem('token');
          const commitmentsRes = await fetch(apiUrl(`/api/commitments/campaign/${campaignId}`), {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          });
          const commitmentsJson = await commitmentsRes.json();
          if (commitmentsJson.success) {
            setFetchedCommitments(commitmentsJson.data || []);
          }
        }
      } catch { /* ignore */ }
      finally { setLoadingData(false); }
    };

    loadData();
  }, [isOpen, campaignId, propCommitments.length]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCompletionMessage('');
      setError('');
      setConfirmed(false);
      setNoDeliveries(false);
      setPhotos([]);
      setCompressing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = maxPhotos - photos.length;
    if (remaining <= 0) return;

    setCompressing(true);
    try {
      const newPhotos: PhotoFile[] = [];
      for (const file of files.slice(0, remaining)) {
        if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) continue;
        let processedFile = file;
        if (file.size > 400 * 1024) {
          processedFile = await compressImage(file, 400);
        }
        newPhotos.push({ file: processedFile, preview: URL.createObjectURL(processedFile) });
      }
      setPhotos(prev => [...prev, ...newPhotos]);
    } catch { /* ignore */ }
    finally {
      setCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const canSubmit = () => {
    if (!completionMessage.trim() || completionMessage.trim().length < 20) return false;
    if (!confirmed) return false;
    if (hasUnresolvedCommitments) return false;
    if (photosRequired && totalPhotos < minPhotos) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Step 1: Upload evidence photos first (if any)
      if (photos.length > 0) {
        const fd = new FormData();
        photos.forEach(p => fd.append('evidenceImages', p.file));

        const uploadRes = await fetch(apiUrl(`/api/campaigns/${campaignId}/evidence`), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd,
        });

        const uploadData = await uploadRes.json();
        if (!uploadRes.ok || !uploadData.success) {
          setError('Error al subir las fotos de evidencia. Intenta de nuevo.');
          setIsSubmitting(false);
          return;
        }
      }

      // Step 2: Complete the campaign (only if evidence uploaded successfully)
      const res = await fetch(apiUrl(`/api/campaigns/${campaignId}/complete`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ completionMessage: completionMessage.trim() }),
      });

      const data = await res.json();

      if (data.success) {
        onSuccess();
      } else {
        setError(data.error?.message || 'Error al finalizar la campaña');
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b rounded-t-xl z-10">
            <h2 className="text-lg font-semibold text-gray-900">Finalizar Campaña</h2>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Loading state */}
            {loadingData && (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-3">Verificando estado de la campaña...</p>
              </div>
            )}

            {!loadingData && (<>
            {/* Unresolved commitments blocker */}
            {hasUnresolvedCommitments && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-900">No puedes finalizar aún</p>
                    <p className="text-xs text-red-700 mt-1">
                      Tienes compromisos sin resolver. Márcalos como entregados o elimínalos antes de cerrar:
                    </p>
                    <ul className="text-xs text-red-700 mt-2 space-y-1">
                      {commitmentSummary.pending > 0 && (
                        <li>• <span className="font-medium">{commitmentSummary.pending}</span> compromiso{commitmentSummary.pending > 1 ? 's' : ''} pendiente{commitmentSummary.pending > 1 ? 's' : ''} de verificar</li>
                      )}
                      {commitmentSummary.verified > 0 && (
                        <li>• <span className="font-medium">{commitmentSummary.verified}</span> compromiso{commitmentSummary.verified > 1 ? 's' : ''} verificado{commitmentSummary.verified > 1 ? 's' : ''} sin marcar como entregado</li>
                      )}
                    </ul>
                    <p className="text-xs text-red-600 mt-2">
                      Ve a la pestaña "Donantes" para resolver estos compromisos.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning */}
            {!hasUnresolvedCommitments && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Esta acción es irreversible</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Al finalizar <span className="font-medium">"{campaignTitle}"</span>, dejará de recibir nuevos compromisos de donación.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Completion message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mensaje de cierre para tu comunidad <span className="text-red-500">*</span>
              </label>
              <textarea
                value={completionMessage}
                onChange={(e) => { setCompletionMessage(e.target.value); if (error) setError(''); }}
                rows={3}
                maxLength={1000}
                disabled={hasUnresolvedCommitments}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Ej: ¡Gracias a todos! Logramos entregar los productos a la familia..."
              />
              <p className={`text-xs mt-1 flex justify-between ${completionMessage.trim().length > 0 && completionMessage.trim().length < 20 ? 'text-red-500' : 'text-gray-400'}`}>
                <span>{completionMessage.trim().length < 20 ? `Mínimo 20 caracteres (${completionMessage.trim().length}/20)` : ''}</span>
                <span>{completionMessage.length}/1000</span>
              </p>
            </div>

            {/* No deliveries exception */}
            {!hasDeliveries && !hasUnresolvedCommitments && (
              <label className="flex items-start space-x-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg p-3">
                <input
                  type="checkbox"
                  checked={noDeliveries}
                  onChange={(e) => setNoDeliveries(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-600">
                  No se recibieron/entregaron donaciones en esta campaña (no se requieren fotos de evidencia)
                </span>
              </label>
            )}

            {/* Evidence photos section */}
            {!hasUnresolvedCommitments && photosRequired && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Camera className="w-4 h-4 inline mr-1" />
                  Fotos de evidencia de la entrega <span className="text-red-500">*</span>
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    (mín. {minPhotos}, máx. {maxPhotos + existingEvidenceCount})
                  </span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Sube al menos {minPhotos} fotos de la entrega para generar confianza con tus donantes. Esta evidencia será visible públicamente.
                </p>

                {existingEvidenceCount > 0 && (
                  <p className="text-xs text-green-600 mb-2">
                    ✅ Ya tienes {existingEvidenceCount} foto{existingEvidenceCount > 1 ? 's' : ''} de evidencia subida{existingEvidenceCount > 1 ? 's' : ''}.
                  </p>
                )}

                <div className="grid grid-cols-3 gap-2">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                      <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button" onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {photos.length < maxPhotos && (
                    <button
                      type="button" onClick={() => fileInputRef.current?.click()}
                      disabled={compressing}
                      className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-indigo-400 flex flex-col items-center justify-center text-gray-400 hover:text-indigo-500 transition-colors"
                    >
                      {compressing ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500" />
                      ) : (
                        <>
                          <Plus className="w-5 h-5" />
                          <span className="text-[10px] mt-1">Foto</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
                  className="hidden" onChange={handleFileSelect}
                />
                <p className="text-xs text-gray-400 mt-2">JPG, PNG o WebP. Se comprimen automáticamente a 400KB.</p>

                {totalPhotos < minPhotos && (
                  <p className="text-xs text-amber-600 mt-1">
                    Faltan {minPhotos - totalPhotos} foto{minPhotos - totalPhotos > 1 ? 's' : ''} más para poder finalizar.
                  </p>
                )}
              </div>
            )}

            {/* Confirmation checkbox */}
            {!hasUnresolvedCommitments && (
              <label className="flex items-start space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => { setConfirmed(e.target.checked); if (error) setError(''); }}
                  className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-600">
                  Entiendo que al finalizar, la campaña dejará de recibir nuevos aportes y no podré revertir esta acción.
                </span>
              </label>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}
            </>)}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white px-6 py-4 border-t flex justify-end gap-3 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {hasUnresolvedCommitments ? 'Cerrar' : 'Cancelar'}
            </button>
            {!hasUnresolvedCommitments && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !canSubmit() || loadingData}
                className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <span>Finalizando...</span>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Finalizar campaña</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
